import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const WebinarSection = () => {
  const upcomingWebinars = [
    {
      id: 1,
      title: "Data Quality Assessment: From Chaos to Clarity",
      description: `Join our expert panel as they walk through a comprehensive data quality assessment methodology. Learn practical techniques for identifying, measuring, and improving data quality across your organization.\n\nThis interactive session includes live Q&A and downloadable assessment templates.`,
      presenter: "Lisa Thompson",
      presenterRole: "Senior Data Quality Consultant",
      presenterImage: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face",
      date: "December 15, 2024",
      time: "2:00 PM GMT",
      duration: "60 minutes",
      registrations: 245,
      maxCapacity: 500,
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=300&fit=crop",
      topics: ["Data Quality", "Assessment", "Methodology"],
      level: "Intermediate"
    },
    {
      id: 2,
      title: "AI Governance: Building Ethical AI Systems",
      description: `Explore the critical aspects of AI governance including bias detection, model explainability, and ethical decision-making frameworks. Perfect for organizations implementing AI solutions.\n\nFeatures case studies from leading organizations and practical implementation guidance.`,
      presenter: "Dr. Michael Chen",
      presenterRole: "AI Ethics Researcher",
      presenterImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      date: "December 22, 2024",
      time: "3:00 PM GMT",
      duration: "90 minutes",
      registrations: 189,
      maxCapacity: 300,
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=300&fit=crop",
      topics: ["AI Governance", "Ethics", "Compliance"],
      level: "Advanced"
    }
  ];

  const pastWebinars = [
    {
      id: 3,
      title: "Data Mesh Implementation: Lessons from the Field",
      presenter: "Sarah Mitchell",
      date: "November 28, 2024",
      views: "1.2k",
      rating: 4.8,
      duration: "75 minutes",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop"
    },
    {
      id: 4,
      title: "GDPR Compliance: 2024 Updates and Best Practices",
      presenter: "Emma Rodriguez",
      date: "November 15, 2024",
      views: "2.1k",
      rating: 4.9,
      duration: "60 minutes",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=200&fit=crop"
    },
    {
      id: 5,
      title: "Building a Data-Driven Culture: Executive Strategies",
      presenter: "David Park",
      date: "November 8, 2024",
      views: "1.8k",
      rating: 4.7,
      duration: "90 minutes",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=200&fit=crop"
    }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-700';
      case 'Intermediate': return 'bg-blue-100 text-blue-700';
      case 'Advanced': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <section className="py-16 lg:py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">Webinars & Events</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join our expert-led sessions and connect with the data community
          </p>
        </div>

        {/* Upcoming Webinars */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-semibold text-primary flex items-center space-x-2">
              <Icon name="Calendar" size={20} />
              <span>Upcoming Webinars</span>
            </h3>
            <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
              View All Events
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {upcomingWebinars?.map((webinar) => (
              <div
                key={webinar?.id}
                className="bg-white rounded-xl shadow-subtle overflow-hidden hover:shadow-elevation hover:scale-[1.02] transition-all duration-300"
              >
                <div className="relative">
                  <Image
                    src={webinar?.image}
                    alt={webinar?.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-secondary text-white px-3 py-1 rounded-full text-sm font-medium">
                      Upcoming
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(webinar?.level)}`}>
                      {webinar?.level}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Image
                      src={webinar?.presenterImage}
                      alt={webinar?.presenter}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium text-primary">{webinar?.presenter}</div>
                      <div className="text-sm text-muted-foreground">{webinar?.presenterRole}</div>
                    </div>
                  </div>

                  <h4 className="text-lg font-semibold text-primary mb-3">
                    {webinar?.title}
                  </h4>

                  <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                    {webinar?.description?.split('\n\n')?.[0]}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="Calendar" size={16} />
                      <span>{webinar?.date}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="Clock" size={16} />
                      <span>{webinar?.time}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="Users" size={16} />
                      <span>{webinar?.registrations}/{webinar?.maxCapacity} registered</span>
                    </div>
                    <div className="flex items-center space-x-2 text-muted-foreground">
                      <Icon name="Timer" size={16} />
                      <span>{webinar?.duration}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {webinar?.topics?.map((topic, index) => (
                      <span
                        key={index}
                        className="bg-surface text-primary px-2 py-1 rounded text-xs font-medium"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {Math.round((webinar?.registrations / webinar?.maxCapacity) * 100)}% full
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Calendar"
                      iconPosition="right"
                      className="bg-secondary hover:bg-secondary/90 hover:scale-105 active:scale-95 transition-all duration-200"
                    >
                      Register Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Past Webinars */}
        <div>
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-semibold text-primary flex items-center space-x-2">
              <Icon name="PlayCircle" size={20} />
              <span>On-Demand Webinars</span>
            </h3>
            <Button variant="outline" size="sm" iconName="Library" iconPosition="left">
              Browse Library
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pastWebinars?.map((webinar) => (
              <div
                key={webinar?.id}
                className="bg-white rounded-xl shadow-subtle overflow-hidden hover:shadow-elevation hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <div className="relative">
                  <Image
                    src={webinar?.image}
                    alt={webinar?.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                    <Icon name="Play" size={32} color="white" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
                    {webinar?.duration}
                  </div>
                </div>

                <div className="p-4">
                  <h4 className="font-semibold text-primary mb-2 line-clamp-2">
                    {webinar?.title}
                  </h4>

                  <div className="text-sm text-muted-foreground mb-3">
                    by {webinar?.presenter} • {webinar?.date}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Icon name="Eye" size={14} />
                        <span>{webinar?.views}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Star" size={14} className="text-yellow-500" />
                        <span>{webinar?.rating}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" iconName="Play" iconPosition="left">
                      Watch
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16 bg-gradient-brand rounded-2xl p-8 lg:p-12 text-center text-white">
          <Icon name="Bell" size={48} className="mx-auto mb-6 text-secondary" />
          <h3 className="text-2xl font-bold mb-4">Never Miss an Event</h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Get notified about upcoming webinars, exclusive events, and new resource releases. 
            Join our community of data professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-4 py-3 rounded-lg text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary"
            />
            <Button
              variant="secondary"
              iconName="ArrowRight"
              iconPosition="right"
              className="whitespace-nowrap"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebinarSection;